import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Text,
    HStack,
    Image,
    Button,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProduct} from '@salesforce/commerce-sdk-react'

import {getUrl} from '../../../utils/cloudinary-utils'
import {useStoreContext} from '../../../contexts/store'
import useTagManager from '../../../hooks/use-tag-manager'

const StoreAvailability = ({product}) => {
    const intl = useIntl()
    const tagManager = useTagManager()
    const {store, onOpen} = useStoreContext()
    const id = product?.id || product?.productId
    const inventoryId = store?.inventoryId || store?.inventoryListID

    const {data: storeAvailability, isLoading} = useProduct(
        {
            parameters: {
                id: id,
                select: '(inventories.(**))',
                inventoryIds: [inventoryId]
            }
        },
        {
            enabled: !!inventoryId
        }
    )
    const isClosed =
        store?.c_BOPIStype === 'disabled' ||
        store?.c_BOPIStype === 'tempdisabled' ||
        store?.bopis === 'disabled' ||
        store?.bopis === 'tempdisabled'
    const isAvailable = storeAvailability?.inventories?.[0]?.orderable

    const handleSelectStore = () => {
        window.scrollTo({top: 0, behavior: 'smooth'})
        tagManager.bopisPDPClick()
        onOpen()
    }
    const storeRef = useRef(store?.ID)
    return (
        <HStack align="start" gap="10px" className="store-availability-wrapper">
            <Image
                src={getUrl('UIImages/images/interface/store-icon.svg')}
                alt=""
                className="store-availability-image"
                width="15px"
                height="15px"
                transform="translateY(5px)"
            />

            {store ? (
                <Box
                    ref={storeRef}
                    className="store-availability-box"
                    data-zip={store?.zip}
                    data-state={store?.stateCode}
                    data-id={store?.ID}
                >
                    <Skeleton
                        isLoaded={!isLoading || isClosed}
                        className="availability-box-skeleton"
                    >
                        <Box
                            className="store-availability-text"
                            color={isAvailable && !isClosed ? 'green.600' : 'red.400'}
                            fontSize="16px"
                            fontWeight="500"
                            display="flex"
                            flexWrap="wrap"
                            letterSpacing="0.08px"
                            lineHeight="25px"
                            gap="5px"
                        >
                            <Text
                                as="span"
                                className="store-availability-span"
                                data-is-closed={isClosed}
                            >
                                {isClosed
                                    ? intl.formatMessage({
                                          id: 'product_view.closed.store',
                                          defaultMessage: 'Unavailable for pick up due to'
                                      })
                                    : isAvailable
                                    ? intl.formatMessage({
                                          id: 'product_view.available_at',
                                          defaultMessage: 'Available for pick up at'
                                      })
                                    : intl.formatMessage({
                                          id: 'product_view.not_available_at',
                                          defaultMessage:
                                              'Buy online, pickup in store is currently unavailable at'
                                      })}
                            </Text>

                            {/* Store name + icon MUST stay together */}
                            <Box display="inline-flex" alignItems="center" gap="8px">
                                <Text
                                    as="span"
                                    textTransform="capitalize"
                                    color="black.100"
                                    textDecoration="underline"
                                    textUnderlineOffset="4px"
                                    cursor="pointer"
                                    onClick={handleSelectStore}
                                    className="store-availability-name"
                                >
                                    {store.name.toLowerCase()}
                                </Text>

                                <Image
                                    src={getUrl('UIImages/images/store-modal.svg')}
                                    onClick={handleSelectStore}
                                    cursor="pointer"
                                    width="14px"
                                    height="14px"
                                    alt="Open Store Modal"
                                />
                            </Box>
                        </Box>
                        {!isAvailable && !isClosed && (
                            <Text
                                color="black.100"
                                marginTop="5px"
                                fontSize="12px"
                                fontWeight="300"
                                letterSpacing="0.06px"
                            >
                                <FormattedMessage
                                    id="call.store.for.availability"
                                    defaultMessage="Call store for availability"
                                />
                            </Text>
                        )}
                    </Skeleton>
                </Box>
            ) : (
                <Box className="store-select-box">
                    <Text
                        color="black.100"
                        className="store-select-text"
                        fontSize="16px"
                        fontWeight="500"
                        lineHeight="25px"
                        letterSpacing="0.08px"
                    >
                        {intl.formatMessage({
                            id: 'product_view.store_availability',
                            defaultMessage: 'Store Availability'
                        })}
                    </Text>

                    <Button
                        onClick={handleSelectStore}
                        variant="linkUnderline"
                        color="black.100"
                        className="store-select-btn"
                        fontSize="16px"
                        fontWeight="300"
                        lineHeight="25px"
                        textUnderlineOffset="4px"
                        textDecoration="underline"
                        letterSpacing="0.08px"
                    >
                        {intl.formatMessage({
                            id: 'product_view.select_a_store',
                            defaultMessage: 'Select a Store'
                        })}
                    </Button>
                </Box>
            )}
        </HStack>
    )
}

StoreAvailability.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string,
        productId: PropTypes.string
    })
}

export default StoreAvailability
